import { HttpErrorResponse } from '@angular/common/http';

/**
 * Extracts a user-meaningful message from an arbitrary thrown value.
 *
 * Resolution order:
 *  1. HttpErrorResponse with a JSON `{ message: string }` body -- backend's GlobalErrorHandlingMiddleware
 *     wraps thrown exceptions (including SoapFaultException) into this shape, so the body carries the
 *     real reason (e.g. a GTX SOAP fault explaining a planner lock).
 *  2. HttpErrorResponse without a usable body -- fall back to the HttpClient-generated message
 *     (typically "Http failure response for ...") rather than swallowing the failure.
 *  3. Plain Error -- use its `.message`.
 *  4. Anything else -- the fallback string.
 *
 * SOAP fault wire framing is stripped so the user-facing modal shows the fault reason directly
 * instead of "SOAP Fault - Code: Sender, Reason: ...".
 *
 * @param error - The value caught from a try/catch or rxjs subscription error handler.
 * @param fallback - Message returned when no other source produced a string. Defaults to a generic sentence.
 * @returns A string suitable for display in a user-facing alert/modal.
 */
export function extractErrorMessage(error: unknown, fallback = 'An unexpected error occurred'): string {
    if (error instanceof HttpErrorResponse) {
        const backendMessage = readBackendMessage(error.error);
        if (backendMessage) {
            return stripSoapFaultPrefix(backendMessage);
        }
        return error.message || fallback;
    }
    if (error instanceof Error) {
        return error.message || fallback;
    }
    return fallback;
}

/**
 * Reads `body.message` only when `body` is an object literal carrying a non-empty string.
 * Defensive against non-JSON bodies, null, arrays, and message fields of the wrong type.
 */
export function readBackendMessage(body: unknown): string | null {
    if (typeof body !== 'object' || body === null || Array.isArray(body)) {
        return null;
    }
    const candidate = (body as { message?: unknown }).message;
    return typeof candidate === 'string' && candidate.length > 0 ? candidate : null;
}

/**
 * GTX SOAP faults bubble up as "SOAP Fault - Code: <code>, Reason: <reason>".
 * Returns just the reason.
 */
function stripSoapFaultPrefix(message: string): string {
    const match = message.match(/^SOAP Fault - Code: [^,]+, Reason: (.+)$/);
    return match ? match[1] : message;
}
